import React, { useCallback, useRef, useState } from 'react';
import { CachedImage } from '@usepepper/ui/atoms/cached-image';
import { createStyles } from '@/contexts/theme-context';
import {
  FlatList,
  Platform,
  TouchableOpacity,
  View,
  ViewabilityConfig,
  ViewToken,
} from 'react-native';
import { ImageViewerModal } from '@/components/image-viewer-modal';
import { ImageWithLoading } from '@/components/image-with-loading';
import { useLayoutContext } from '@/contexts/layout-context';

export const DEFAULT_IMAGE_SIZE = 210;
export const LARGE_IMAGE_SIZE = 370;
const IMAGE_PADDING_HORIZONTAL = 4;
const IMAGE_PREVIEW_HIT_SLOP = {
  bottom: 4,
  left: 4,
  right: 4,
  top: 4,
};
const VIEWABILITY_CONFIG: ViewabilityConfig = {
  itemVisiblePercentThreshold: 50,
};

interface Props {
  alt: string;
  imageURLs: string[];
  size: number;
}

export const ItemDetailsImageList: React.FC<Props> = ({
  alt,
  imageURLs,
  size,
}) => {
  const [styles] = useStyles();
  const { isLargeLayout } = useLayoutContext();

  const listRef = useRef<FlatList>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [showViewer, setShowViewer] = useState(false);

  const onViewableItemsChangedCallback = useCallback(
    (info: { viewableItems: ViewToken[] }) => {
      const nextActiveIndex = info.viewableItems[0]?.index;
      if (typeof nextActiveIndex === 'number') {
        setActiveIndex(nextActiveIndex);
      }
    },
    [],
  );

  if (!imageURLs.length) {
    return null;
  }

  const listItemWidth = size + 2 * IMAGE_PADDING_HORIZONTAL;

  return (
    <View style={styles.base}>
      <FlatList
        alwaysBounceHorizontal={false}
        alwaysBounceVertical={false}
        data={imageURLs}
        getItemLayout={(_, index) => ({
          index,
          length: listItemWidth,
          offset: listItemWidth * index,
        })}
        horizontal
        keyExtractor={(item) => item}
        onViewableItemsChanged={onViewableItemsChangedCallback}
        pagingEnabled
        ref={listRef}
        renderItem={({ item, index }) => {
          return (
            <TouchableOpacity
              activeOpacity={1}
              onPress={() => {
                if (Platform.OS !== 'web') {
                  setShowViewer(true);
                }
              }}
              style={styles.imageButton}
            >
              <ImageWithLoading
                alt={`${alt} - Image ${index + 1}`}
                contentFit="contain"
                source={{ uri: item }}
                style={{ height: size, width: size }}
                testID="item-details-image"
              />
            </TouchableOpacity>
          );
        }}
        showsHorizontalScrollIndicator={false}
        style={{ height: size, width: listItemWidth }}
        viewabilityConfig={VIEWABILITY_CONFIG}
      />
      {imageURLs.length > 1 ? (
        <View style={styles.imagePreviews}>
          {imageURLs.map((imageURL, index) => {
            const isActive = index === activeIndex;
            return (
              <TouchableOpacity
                activeOpacity={1}
                disabled={isActive}
                hitSlop={IMAGE_PREVIEW_HIT_SLOP}
                key={imageURL}
                onPress={() => {
                  listRef.current?.scrollToIndex({ index });
                }}
                style={[
                  styles.imagePreviewButton,
                  isActive ? styles.imagePreviewButtonActive : null,
                ]}
              >
                <CachedImage
                  alt={`${alt} - Image ${index + 1}`}
                  contentFit="cover"
                  source={{ uri: imageURL }}
                  style={
                    isLargeLayout
                      ? styles.imagePreviewLarge
                      : styles.imagePreview
                  }
                  testID="item-details-image-preview"
                />
              </TouchableOpacity>
            );
          })}
        </View>
      ) : null}
      <ImageViewerModal
        imageURLs={imageURLs}
        initialIndex={activeIndex}
        isVisible={showViewer}
        onClose={() => setShowViewer(false)}
      />
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  base: {
    alignItems: 'center',
  },
  imageButton: {
    cursor: 'auto',
    paddingHorizontal: IMAGE_PADDING_HORIZONTAL,
  },
  imagePreview: {
    height: 32,
    width: 32,
  },
  imagePreviewButton: {
    borderColor: theme.colors.white,
    borderRadius: 4,
    borderWidth: 2,
    overflow: 'hidden',
  },
  imagePreviewButtonActive: {
    borderColor: theme.colors.accent,
    borderRadius: 4,
  },
  imagePreviewLarge: {
    height: 40,
    width: 40,
  },
  imagePreviews: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    justifyContent: 'center',
    marginTop: 8,
  },
}));
